import React from 'react';
import { Sparkles } from 'lucide-react';
import type { DocumentAnalysisResult } from '../../types/documentIntelligence';
import { fieldDisplayValue, formatConfidence, isLowConfidence } from '../../types/documentIntelligence';

interface VaultIntelligenceReviewProps {
  analysis: DocumentAnalysisResult | null;
  loading: boolean;
  stage: string | null;
  name: string;
  description: string;
  categoryId: string;
  subcategory: string;
  tagsInput: string;
  expiryDate: string;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onSubcategoryChange: (value: string) => void;
  onTagsChange: (value: string) => void;
  onExpiryChange: (value: string) => void;
  categories: Array<{ id: string; label: string; items: string[] }>;
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  if (confidence <= 0) return null;
  return (
    <span className={`wfx-ai-confidence${isLowConfidence(confidence) ? ' is-low' : ''}`}>
      {formatConfidence(confidence)}
    </span>
  );
}

function ReviewField({
  label,
  confidence,
  children,
}: {
  label: string;
  confidence?: number;
  children: React.ReactNode;
}) {
  return (
    <label className="auth-field wfx-ai-review-field">
      <span className="wfx-field-label wfx-ai-field-head">
        <span>{label}</span>
        {confidence != null && confidence > 0 && <ConfidenceBadge confidence={confidence} />}
      </span>
      {children}
    </label>
  );
}

export function VaultIntelligenceReview({
  analysis,
  loading,
  stage,
  name,
  description,
  categoryId,
  subcategory,
  tagsInput,
  expiryDate,
  onNameChange,
  onDescriptionChange,
  onCategoryChange,
  onSubcategoryChange,
  onTagsChange,
  onExpiryChange,
  categories,
}: VaultIntelligenceReviewProps) {
  const category = categories.find((c) => c.id === categoryId);
  const showEventFields = Boolean(
    analysis?.eventName.value || analysis?.recognition.value || analysis?.organizer.value,
  );
  const showResearch = Boolean(
    analysis?.research?.title?.value || analysis?.categoryId?.value === 'research',
  );

  return (
    <div className="wfx-ai-review">
      {loading && stage && (
        <div className="wfx-upload-ai-progress glass-surface">
          <Sparkles size={18} aria-hidden="true" />
          <span>{stage}</span>
        </div>
      )}

      {analysis && !loading && (
        <div className="wfx-ai-review-summary glass-surface">
          <Sparkles size={16} aria-hidden="true" />
          <div>
            <strong>AI Document Intelligence</strong>
            <p className="wfx-muted">
              {analysis.documentType.value || 'Document analyzed'} · {formatConfidence(analysis.overallConfidence)} overall
            </p>
          </div>
        </div>
      )}

      <ReviewField label="Document type" confidence={analysis?.documentType.confidence}>
        <input
          type="text"
          value={analysis?.documentType.value ?? subcategory}
          readOnly
          className="wfx-ai-readonly"
        />
      </ReviewField>

      <ReviewField label="Document name" confidence={analysis?.name.confidence}>
        <input type="text" required value={name} onChange={(e) => onNameChange(e.target.value)} />
      </ReviewField>

      <div className="wfx-vault-upload-row">
        <ReviewField label="Category" confidence={analysis?.categoryId.confidence}>
          <select value={categoryId} onChange={(e) => onCategoryChange(e.target.value)} required>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
        </ReviewField>
        <ReviewField label="Type" confidence={analysis?.subcategory.confidence}>
          <input type="text" required value={subcategory} onChange={(e) => onSubcategoryChange(e.target.value)} />
        </ReviewField>
      </div>

      <ReviewField label="Description" confidence={analysis?.description.confidence}>
        <textarea
          rows={3}
          placeholder="AI-generated description"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
        />
      </ReviewField>

      {showEventFields && (
        <div className="wfx-ai-review-group glass-surface">
          <h3>Event & recognition</h3>
          {analysis?.eventName.value && (
            <p><span className="wfx-muted">Event</span> {fieldDisplayValue(analysis.eventName)} <ConfidenceBadge confidence={analysis.eventName.confidence} /></p>
          )}
          {analysis?.eventType.value && (
            <p><span className="wfx-muted">Event type</span> {fieldDisplayValue(analysis.eventType)} <ConfidenceBadge confidence={analysis.eventType.confidence} /></p>
          )}
          {analysis?.recognition.value && (
            <p><span className="wfx-muted">Recognition</span> {fieldDisplayValue(analysis.recognition)} <ConfidenceBadge confidence={analysis.recognition.confidence} /></p>
          )}
          {analysis?.recognitionDetail.value && (
            <p><span className="wfx-muted">Detail</span> {fieldDisplayValue(analysis.recognitionDetail)} <ConfidenceBadge confidence={analysis.recognitionDetail.confidence} /></p>
          )}
          {analysis?.organizer.value && (
            <p><span className="wfx-muted">Organizer</span> {fieldDisplayValue(analysis.organizer)} <ConfidenceBadge confidence={analysis.organizer.confidence} /></p>
          )}
          {analysis?.location.value && (
            <p><span className="wfx-muted">Location</span> {fieldDisplayValue(analysis.location)} <ConfidenceBadge confidence={analysis.location.confidence} /></p>
          )}
          {analysis?.level.value && (
            <p><span className="wfx-muted">Level</span> {fieldDisplayValue(analysis.level)} <ConfidenceBadge confidence={analysis.level.confidence} /></p>
          )}
        </div>
      )}

      {showResearch && analysis?.research && (
        <div className="wfx-ai-review-group glass-surface">
          <h3>Research metadata</h3>
          {analysis.research.title.value && (
            <p><span className="wfx-muted">Title</span> {fieldDisplayValue(analysis.research.title)} <ConfidenceBadge confidence={analysis.research.title.confidence} /></p>
          )}
          {analysis.research.authors.value && analysis.research.authors.value.length > 0 && (
            <p><span className="wfx-muted">Authors</span> {fieldDisplayValue(analysis.research.authors)} <ConfidenceBadge confidence={analysis.research.authors.confidence} /></p>
          )}
          {analysis.research.journalName.value && (
            <p><span className="wfx-muted">Journal</span> {fieldDisplayValue(analysis.research.journalName)} <ConfidenceBadge confidence={analysis.research.journalName.confidence} /></p>
          )}
          {analysis.research.doi.value && (
            <p><span className="wfx-muted">DOI</span> {fieldDisplayValue(analysis.research.doi)} <ConfidenceBadge confidence={analysis.research.doi.confidence} /></p>
          )}
        </div>
      )}

      <ReviewField label="Tags" confidence={analysis?.tags.confidence}>
        <input
          type="text"
          placeholder="Psychology, Research, Conference"
          value={tagsInput}
          onChange={(e) => onTagsChange(e.target.value)}
        />
      </ReviewField>

      {(categoryId === 'certificates' || categoryId === 'identity' || analysis?.expiryDate.value) && (
        <ReviewField label="Expiry date" confidence={analysis?.expiryDate.confidence}>
          <input type="date" value={expiryDate} onChange={(e) => onExpiryChange(e.target.value)} />
        </ReviewField>
      )}

      {analysis && !loading && (
        <p className="wfx-ai-review-note wfx-muted">
          Fields below {80}% confidence are highlighted — please verify before saving.
        </p>
      )}
    </div>
  );
}
